import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Firm, ModelCost, ModelOverview, ModelPriceHistory, Stock } from "../types";
import { modelService, firmService } from "../services/dataService";
import api, { getMamulStocks } from "../services/api";
import { formatCurrency, formatNumber, getImageUrl, turkishIncludes } from "../utils/formatters";
import { useExchangeRates } from "../context/ExchangeRateContext";
import { usePermissions } from "../context/PermissionContext";
import PageLoader from "../components/PageLoader";
import AlertModal from "../components/AlertModal";
import ConfirmModal from "../components/ConfirmModal";
import FirmPickerField from "../components/FirmPicker";
import ModelCostsModal from "../components/ModelCostsModal";
import "./Models.css";

/**
 * Model Yönetimi — fotoğraf öncelikli katalog.
 *
 * Veri tek istekten gelir (GET /Model/overview): model görseli + sipariş
 * görselleri, sipariş sayıları, son sipariş fiyatı ve son maliyet setinin
 * TL karşılığı (satırın kaydedildiği kurla). Kart ızgarası ve liste görünümü
 * aynı veriyi kullanır; detay çekmecesi maliyet kırılımını ve fiyat geçmişini
 * açılınca yükler. Modellerin çoğunun kendi görseli yoktur; fotoğraflar sipariş
 * görsellerinden gelir.
 */

/* ---------- yardımcılar ---------- */

type View = "grid" | "list";
type Chip = "" | "photo" | "nophoto" | "cost" | "nocost" | "active";
type Sort = "code" | "recent" | "cost" | "orders" | "margin";

const PAGE = 48;
const money = (v?: number | null, cur?: string | null) => (v == null ? "—" : formatCurrency(v, cur || "TRY"));
const fmtDate = (s?: string | null) => (s ? new Date(s).toLocaleDateString("tr-TR", { day: "2-digit", month: "short", year: "numeric" }) : "—");
/** Monogram: kodun ayırt edici kuyruğu ("27-9029" → "9029"); tire yoksa ilk 5 karakter */
const initials = (code: string) => {
  const raw = (code || "").trim();
  const tail = raw.includes("-") ? raw.split("-").pop() || raw : raw;
  return tail.replace(/[^A-Za-z0-9ÇĞİÖŞÜçğıöşü]/g, "").slice(0, 5).toUpperCase() || "?";
};
const hue = (s: string) => { let h = 0; for (const ch of s) h = (h * 31 + ch.charCodeAt(0)) % 360; return h; };

const errorText = (error: any, fallback: string) => {
  const d = error?.response?.data;
  if (typeof d === "string") return d;
  return d?.detail || d?.title || d?.message || error?.message || fallback;
};

const I = {
  search: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>,
  plus: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>,
  refresh: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 1 1-2.64-6.36" /><path d="M21 3v6h-6" /></svg>,
  close: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6 6 18" /></svg>,
  edit: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" /></svg>,
  trash: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14" /></svg>,
  eye: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z" /><circle cx="12" cy="12" r="3" /></svg>,
  cost: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="9" /><path d="M14.5 9.5a2.5 2.5 0 0 0-5 0c0 1.5 1.2 2 2.5 2.5s2.5 1 2.5 2.5a2.5 2.5 0 0 1-5 0M12 6v1.5M12 16.5V18" /></svg>,
  grid: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" /></svg>,
  list: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M4 6h16M4 12h16M4 18h16" /></svg>,
  image: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="16" rx="2" /><circle cx="9" cy="10" r="1.8" /><path d="m21 17-5-5-7 7" /></svg>,
  filterOff: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 5h18l-7 8v6l-4-2v-4L3 5Z" /></svg>,
  inbox: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16v16H4z" /><path d="M4 13h5l2 3h2l2-3h5" /></svg>,
  arrow: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6" /></svg>,
  chevL: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 6-6 6 6 6" /></svg>,
  chevR: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 6 6 6-6 6" /></svg>,
};

const Field: React.FC<{ label: string; hint?: string; error?: string; children: React.ReactNode }> = ({ label, hint, error, children }) => (
  <label className={`mo-field ${error ? "err" : ""}`}>
    <span>{label}{hint && <em>{hint}</em>}</span>
    {children}
    {error && <small>{error}</small>}
  </label>
);

/** Görsel yüklenemezse (dosya silinmiş, sunucuda yok) monograma düşer */
const Img: React.FC<{ src: string; alt: string; fallback: React.ReactNode; className?: string }> = ({ src, alt, fallback, className }) => {
  const [broken, setBroken] = useState(false);
  useEffect(() => { setBroken(false); }, [src]);
  if (broken) return <>{fallback}</>;
  return <img src={src} alt={alt} loading="lazy" className={className} onError={() => setBroken(true)} />;
};

/** Fotoğraf ya da monogram — kartta ve listede aynı bileşen */
const Photo: React.FC<{ m: ModelOverview; className?: string; onClick?: () => void }> = ({ m, className, onClick }) => {
  const src = m.imageUrls[0];
  const [broken, setBroken] = useState(false);
  useEffect(() => { setBroken(false); }, [src]);
  const show = !!src && !broken;
  return (
    <div className={`mo-photo ${className || ""} ${show ? "" : "empty"}`} style={{ ["--mo-hue" as any]: hue(m.modelCode) }} onClick={onClick}>
      {show ? <img src={getImageUrl(src)} alt={m.modelName} loading="lazy" onError={() => setBroken(true)} /> : <span className="mo-mono">{initials(m.modelCode)}</span>}
      {show && m.imageUrls.length > 1 && <span className="mo-photo-count">{I.image} {m.imageUrls.length}</span>}
    </div>
  );
};

/* ---------- sayfa ---------- */

const emptyForm = { firmId: "", modelCode: "", modelName: "", description: "", imageUrl: "", productionStockId: "" };
type Form = typeof emptyForm;

const Models: React.FC = () => {
  const { has, hasModule } = usePermissions();
  const { usdRate, eurRate, gbpRate } = useExchangeRates();
  const canCosts = has("model-costs:view") || has("cost:items:view");

  const [models, setModels] = useState<ModelOverview[]>([]);
  const [rates, setRates] = useState({ overhead: 0, profit: 0 });
  const [firms, setFirms] = useState<Firm[]>([]);
  const [mamul, setMamul] = useState<Stock[]>([]);
  const [initial, setInitial] = useState(true);
  const [busy, setBusy] = useState(false);
  const [alert, setAlert] = useState<{ isOpen: boolean; message: string; type: "info" | "success" | "warning" | "error" }>({ isOpen: false, message: "", type: "info" });
  const [confirm, setConfirm] = useState<{ isOpen: boolean; message: string; onConfirm: () => void }>({ isOpen: false, message: "", onConfirm: () => {} });

  const [view, setView] = useState<View>(() => { try { return (localStorage.getItem("mo-view") as View) || "grid"; } catch { return "grid"; } });
  const [q, setQ] = useState("");
  const [firmFilter, setFirmFilter] = useState("");
  const [chip, setChip] = useState<Chip>("");
  const [sort, setSort] = useState<Sort>("code");
  const [shown, setShown] = useState(PAGE);

  const [detail, setDetail] = useState<ModelOverview | null>(null);
  const [detailImg, setDetailImg] = useState(0);
  const [detailCosts, setDetailCosts] = useState<ModelCost[] | null>(null);
  const [detailPrices, setDetailPrices] = useState<ModelPriceHistory | null>(null);
  const [lightbox, setLightbox] = useState<string | null>(null);

  const [costsOf, setCostsOf] = useState<ModelOverview | null>(null);
  const [modal, setModal] = useState<{ open: boolean; editing: ModelOverview | null; form: Form; errors: Partial<Form> }>({ open: false, editing: null, form: emptyForm, errors: {} });

  /* ---- yükleme ---- */

  const load = useCallback(async () => {
    try {
      const [ov, fl] = await Promise.all([modelService.overview(), firmService.getFirms()]);
      setModels(ov.models);
      setRates({ overhead: ov.overheadRate, profit: ov.profitMargin });
      setFirms(fl);
    } catch (err) {
      setAlert({ isOpen: true, message: errorText(err, "Modeller yüklenemedi."), type: "error" });
    } finally {
      setInitial(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!hasModule("stock")) return;
    getMamulStocks().then((r) => setMamul(r.data || [])).catch(() => setMamul([]));
  }, [hasModule]);
  useEffect(() => { try { localStorage.setItem("mo-view", view); } catch { /* yok say */ } }, [view]);

  const refresh = async () => { setBusy(true); await load(); setBusy(false); };

  /* ---- türetilmiş ---- */

  /** Son satış fiyatının TL karşılığı — bugünkü kurla; kur yoksa null */
  const priceTry = useCallback((m: ModelOverview): number | null => {
    const p = m.lastOrderPrice ?? m.price ?? null;
    if (p == null) return null;
    const cur = (m.lastOrderPrice != null ? m.lastOrderCurrency : m.priceCurrency) || "TRY";
    if (cur === "TRY") return p;
    const r = cur === "USD" ? usdRate : cur === "EUR" ? eurRate : cur === "GBP" ? gbpRate : null;
    return r ? p * r : null;
  }, [usdRate, eurRate, gbpRate]);

  /** Brüt marj: (satış − birim maliyet) / satış */
  const margin = useCallback((m: ModelOverview): number | null => {
    const p = priceTry(m);
    if (p == null || p <= 0 || m.lastCostUnitTry == null) return null;
    return ((p - m.lastCostUnitTry) / p) * 100;
  }, [priceTry]);

  const firmsInUse = useMemo(() => {
    const ids = new Set(models.map((m) => m.firmId).filter(Boolean));
    return firms.filter((f) => ids.has(f.firmId)).sort((a, b) => a.firmName.localeCompare(b.firmName, "tr"));
  }, [models, firms]);

  const filtered = useMemo(() => {
    const list = models.filter((m) => {
      if (firmFilter && m.firmId !== firmFilter) return false;
      if (chip === "photo" && m.imageUrls.length === 0) return false;
      if (chip === "nophoto" && m.imageUrls.length > 0) return false;
      if (chip === "cost" && m.lastCostTotalTry == null) return false;
      if (chip === "nocost" && m.lastCostTotalTry != null) return false;
      if (chip === "active" && m.activeOrderCount === 0) return false;
      if (q) {
        const hit = turkishIncludes(m.modelCode, q) || turkishIncludes(m.modelName, q) || turkishIncludes(m.firmName || "", q)
          || turkishIncludes(m.firmCode || "", q) || turkishIncludes(m.patternCode || "", q) || turkishIncludes(m.category || "", q) || turkishIncludes(m.description || "", q);
        if (!hit) return false;
      }
      return true;
    });
    const t = (s?: string | null) => (s ? new Date(s).getTime() : 0);
    switch (sort) {
      case "recent": list.sort((a, b) => t(b.lastOrderDate) - t(a.lastOrderDate)); break;
      case "cost": list.sort((a, b) => (b.lastCostUnitTry ?? -1) - (a.lastCostUnitTry ?? -1)); break;
      case "orders": list.sort((a, b) => b.orderCount - a.orderCount || b.activeOrderCount - a.activeOrderCount); break;
      case "margin": list.sort((a, b) => (margin(b) ?? -999) - (margin(a) ?? -999)); break;
      default: list.sort((a, b) => a.modelCode.localeCompare(b.modelCode, "tr", { numeric: true }));
    }
    return list;
  }, [models, firmFilter, chip, q, sort, margin]);

  useEffect(() => { setShown(PAGE); }, [q, firmFilter, chip, sort, view]);
  const visible = filtered.slice(0, shown);

  const kpi = useMemo(() => {
    const withPhoto = models.filter((m) => m.imageUrls.length > 0).length;
    const withCost = models.filter((m) => m.lastCostTotalTry != null).length;
    const active = models.filter((m) => m.activeOrderCount > 0).length;
    const firmsN = new Set(models.map((m) => m.firmId).filter(Boolean)).size;
    return { total: models.length, withPhoto, withCost, active, firmsN };
  }, [models]);

  const filterOn = !!(q || firmFilter || chip);
  const clearFilters = () => { setQ(""); setFirmFilter(""); setChip(""); };

  /* ---- çekmece ---- */

  const openDetail = async (m: ModelOverview) => {
    setDetail(m); setDetailImg(0); setDetailCosts(null); setDetailPrices(null);
    const jobs: Promise<any>[] = [];
    if (canCosts && m.lastCostOrderId) jobs.push(api.get<ModelCost[]>(`/Cost/model/${m.modelId}`).then((r) => setDetailCosts(r.data || [])).catch(() => setDetailCosts([])));
    jobs.push(modelService.getPriceHistory(m.modelId).then(setDetailPrices).catch(() => setDetailPrices(null)));
    await Promise.all(jobs);
  };
  useEffect(() => {
    if (!detail && !lightbox) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { if (lightbox) setLightbox(null); else setDetail(null); }
      if (detail && !lightbox && detail.imageUrls.length > 1) {
        if (e.key === "ArrowRight") setDetailImg((i) => (i + 1) % detail.imageUrls.length);
        if (e.key === "ArrowLeft") setDetailImg((i) => (i - 1 + detail.imageUrls.length) % detail.imageUrls.length);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [detail, lightbox]);

  /** Son maliyet setinin kategori kırılımı (TL) — çekmece için */
  const breakdown = useMemo(() => {
    if (!detail || !detailCosts) return null;
    const rows = detailCosts.filter((c) => c.orderId === detail.lastCostOrderId);
    const toTry = (c: ModelCost) => {
      const amt = c.totalCost || 0; const cur = (c.currency || "TRY").toUpperCase();
      const r = cur === "TRY" ? 1 : cur === "USD" ? c.usdRate : cur === "EUR" ? c.eurRate : cur === "GBP" ? c.gbpRate : null;
      return r ? amt * r : null;
    };
    const cats = new Map<string, { total: number; items: { name: string; qty: string; total: number | null; cur: string; amount: number }[] }>();
    let grand = 0;
    for (const c of rows) {
      const k = c.costCategoryName || "Diğer";
      if (!cats.has(k)) cats.set(k, { total: 0, items: [] });
      const t = toTry(c); const e = cats.get(k)!;
      if (t != null) { e.total += t; grand += t; }
      e.items.push({ name: c.costItemName || "—", qty: `${formatNumber(c.quantity, 2)} ${c.unit || ""}${c.quantity2 ? ` × ${formatNumber(c.quantity2, 2)} ${c.unit2 || ""}` : ""}`.trim(), total: t, cur: c.currency || "TRY", amount: c.totalCost || 0 });
    }
    return { grand, cats: Array.from(cats.entries()).map(([name, v]) => ({ name, ...v })).sort((a, b) => b.total - a.total), count: rows.length };
  }, [detail, detailCosts]);

  /* ---- form ---- */

  const openNew = () => setModal({ open: true, editing: null, form: { ...emptyForm, firmId: firmFilter || "" }, errors: {} });
  const openEdit = (m: ModelOverview) => setModal({ open: true, editing: m, form: { firmId: m.firmId || "", modelCode: m.modelCode, modelName: m.modelName, description: m.description || "", imageUrl: m.imageUrl || "", productionStockId: m.productionStockId || "" }, errors: {} });
  const setF = (patch: Partial<Form>) => setModal((x) => ({ ...x, form: { ...x.form, ...patch } }));

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    const f = modal.form; const errors: Partial<Form> = {};
    if (!f.firmId) errors.firmId = "Firma seçimi zorunludur";
    if (!f.modelCode.trim()) errors.modelCode = "Model kodu zorunludur";
    if (!f.modelName.trim()) errors.modelName = "Model adı zorunludur";
    if (Object.keys(errors).length) { setModal((x) => ({ ...x, errors })); return; }
    setBusy(true);
    try {
      const data: any = { firmId: f.firmId, modelCode: f.modelCode.trim(), modelName: f.modelName.trim(), description: f.description.trim() || undefined, imageUrl: f.imageUrl.trim() || undefined, productionStockId: f.productionStockId || null };
      if (modal.editing) { data.modelId = modal.editing.modelId; await modelService.update(modal.editing.modelId, data); setAlert({ isOpen: true, message: "Model güncellendi.", type: "success" }); }
      else { await modelService.create(data); setAlert({ isOpen: true, message: "Model oluşturuldu.", type: "success" }); }
      setModal((x) => ({ ...x, open: false }));
      await load();
      // Açık çekmece varsa yeni veriyle tazelenir (load sonrası models state'i bir tık sonra gelir)
      if (detail && modal.editing?.modelId === detail.modelId) {
        const fresh = (await modelService.overview()).models.find((m) => m.modelId === detail.modelId);
        if (fresh) setDetail(fresh);
      }
    } catch (err) { setAlert({ isOpen: true, message: errorText(err, "Model kaydedilemedi."), type: "error" }); }
    finally { setBusy(false); }
  };

  const remove = (m: ModelOverview) => {
    if (m.orderCount > 0) { setAlert({ isOpen: true, message: `"${m.modelName}" modeline bağlı ${m.orderCount} sipariş var; siparişi olan model silinemez.`, type: "warning" }); return; }
    setConfirm({
      isOpen: true, message: `"${m.modelName}" (${m.modelCode}) modeli kalıcı olarak silinecek.`,
      onConfirm: async () => {
        setConfirm((c) => ({ ...c, isOpen: false })); setBusy(true);
        try { await modelService.delete(m.modelId); setAlert({ isOpen: true, message: "Model silindi.", type: "success" }); if (detail?.modelId === m.modelId) setDetail(null); await load(); }
        catch (err) { setAlert({ isOpen: true, message: errorText(err, "Model silinemedi."), type: "error" }); }
        finally { setBusy(false); }
      },
    });
  };

  if (initial) return <PageLoader message="Modeller yükleniyor..." />;

  /* ---------- parçalar ---------- */

  const marginBadge = (m: ModelOverview) => {
    const mg = margin(m);
    if (mg == null) return null;
    const cls = mg >= rates.profit ? "ok" : mg > 0 ? "warn" : "err";
    return <span className={`mo-margin ${cls}`} title="Brüt marj: (son satış − birim maliyet) / son satış">%{formatNumber(mg, 0)} marj</span>;
  };

  const costLine = (m: ModelOverview) =>
    m.lastCostTotalTry != null ? (
      <div className="mo-cost">
        <div className="mo-cost-main"><span className="l">Birim maliyet</span><b>{m.lastCostHasUnknownRate ? "~" : ""}{money(m.lastCostUnitTry)}</b></div>
        <div className="mo-cost-sub">{m.lastCostItemCount} kalem · {formatNumber(m.lastCostQuantity || 0, 0)} adet · {fmtDate(m.lastCostDate)}</div>
      </div>
    ) : (
      <div className="mo-cost empty"><span>Maliyet girilmemiş</span>{canCosts && <button className="mo-link" onClick={(e) => { e.stopPropagation(); setCostsOf(m); }}>Maliyet gir {I.arrow}</button>}</div>
    );

  const priceLine = (m: ModelOverview) => {
    const p = m.lastOrderPrice ?? m.price;
    if (p == null) return <span className="mo-sub">Satış fiyatı yok</span>;
    return <span className="mo-price">Son satış <b>{money(p, m.lastOrderPrice != null ? m.lastOrderCurrency : m.priceCurrency)}</b></span>;
  };

  const actions = (m: ModelOverview) => (
    <div className="mo-acts" onClick={(e) => e.stopPropagation()}>
      <button className="mo-ib" title="Detay" onClick={() => openDetail(m)}>{I.eye}</button>
      {canCosts && <button className="mo-ib" title="Maliyetler" onClick={() => setCostsOf(m)}>{I.cost}</button>}
      {has("models:update") && <button className="mo-ib" title="Düzenle" onClick={() => openEdit(m)}>{I.edit}</button>}
      {has("models:delete") && <button className="mo-ib danger" title="Sil" onClick={() => remove(m)}>{I.trash}</button>}
    </div>
  );

  const card = (m: ModelOverview) => (
    <article key={m.modelId} className={`mo-card ${detail?.modelId === m.modelId ? "on" : ""}`} onClick={() => openDetail(m)}>
      <Photo m={m} className="cover" />
      <div className="mo-card-badges">
        {m.firmCode && <span className="mo-tag firm">{m.firmCode}</span>}
        {m.activeOrderCount > 0 && <span className="mo-tag live">{m.activeOrderCount} aktif sipariş</span>}
      </div>
      <div className="mo-card-body">
        <div className="mo-code">{m.modelCode}</div>
        <h3 className="mo-name" title={m.modelName}>{m.modelName}</h3>
        <div className="mo-firm">{m.firmName || "Firma atanmamış"}{m.season ? ` · ${m.season}` : ""}{m.category ? ` · ${m.category}` : ""}</div>
        {costLine(m)}
        <div className="mo-card-foot">{priceLine(m)}{marginBadge(m)}</div>
      </div>
      <div className="mo-card-hover">{actions(m)}</div>
    </article>
  );

  const row = (m: ModelOverview) => (
    <tr key={m.modelId} className={detail?.modelId === m.modelId ? "on" : ""} onClick={() => openDetail(m)}>
      <td className="thumb"><Photo m={m} className="mini" /></td>
      <td><div className="mo-code">{m.modelCode}</div><div className="mo-name-sm">{m.modelName}</div></td>
      <td className="hide-md">{m.firmName || "—"}{m.firmCode && <div className="mo-sub">{m.firmCode}</div>}</td>
      <td className="hide-lg">{[m.category, m.season].filter(Boolean).join(" · ") || "—"}</td>
      <td className="num">{m.orderCount}{m.activeOrderCount > 0 && <span className="mo-tag live sm">{m.activeOrderCount} aktif</span>}</td>
      <td className="num">{m.lastCostUnitTry != null ? <><b>{m.lastCostHasUnknownRate ? "~" : ""}{money(m.lastCostUnitTry)}</b><div className="mo-sub">{fmtDate(m.lastCostDate)}</div></> : <span className="mo-sub">—</span>}</td>
      <td className="num hide-md">{(m.lastOrderPrice ?? m.price) != null ? money(m.lastOrderPrice ?? m.price, m.lastOrderPrice != null ? m.lastOrderCurrency : m.priceCurrency) : "—"}</td>
      <td className="hide-sm">{marginBadge(m) || <span className="mo-sub">—</span>}</td>
      <td className="act">{actions(m)}</td>
    </tr>
  );

  /* ---------- görünüm ---------- */

  return (
    <div className="mo-page">
      <header className="mo-head">
        <div>
          <span className="mo-kicker">Tanımlamalar</span>
          <h1>Model Yönetimi</h1>
          <p>Firmalara ait modeller, fotoğrafları, son sipariş fiyatı ve son maliyet setine göre birim maliyet. Fotoğraflar siparişlerdeki görsellerden gelir.</p>
        </div>
        <div className="mo-head-actions">
          <button className="mo-btn" onClick={refresh} disabled={busy}><span className={busy ? "mo-spin-icon" : ""}>{I.refresh}</span> Yenile</button>
          <div className="mo-seg" role="group" aria-label="Görünüm">
            <button className={view === "grid" ? "on" : ""} onClick={() => setView("grid")} title="Kart görünümü">{I.grid}</button>
            <button className={view === "list" ? "on" : ""} onClick={() => setView("list")} title="Liste görünümü">{I.list}</button>
          </div>
          {has("models:create") && <button className="mo-btn primary" onClick={openNew}>{I.plus} Yeni model</button>}
        </div>
      </header>

      <section className="mo-kpis">
        <div className="mo-kpi navy"><span className="l">Model</span><span className="v">{kpi.total}</span><span className="s">{kpi.firmsN} firma</span></div>
        <div className="mo-kpi"><span className="l">Fotoğraflı</span><span className="v">{kpi.withPhoto}</span><span className="s">{kpi.total ? Math.round((kpi.withPhoto / kpi.total) * 100) : 0}% · sipariş görsellerinden</span></div>
        <div className="mo-kpi"><span className="l">Maliyeti girilmiş</span><span className="v">{kpi.withCost}</span><span className="s">{kpi.total - kpi.withCost} modelde maliyet yok</span></div>
        <div className={`mo-kpi ${kpi.active ? "ok" : ""}`}><span className="l">Aktif siparişli</span><span className="v">{kpi.active}</span><span className="s">tamamlanmamış siparişi olan</span></div>
      </section>

      <section className="mo-toolbar">
        <div className="mo-search">{I.search}<input placeholder="Kod, ad, firma, desen kodu veya kategori ara" value={q} onChange={(e) => setQ(e.target.value)} />{q && <button className="mo-ib" aria-label="Temizle" onClick={() => setQ("")}>{I.close}</button>}</div>
        <FirmPickerField variant="toolbar" value={firmFilter} firms={firmsInUse} nullLabel="Tüm firmalar" allowCreate={false} title="Firmaya göre süz" onChange={(f) => setFirmFilter(f?.firmId || "")} />
        <div className="mo-chipgroup">
          {([["", "Tümü"], ["photo", "Fotoğraflı"], ["cost", "Maliyetli"], ["nocost", "Maliyetsiz"], ["active", "Aktif siparişli"]] as [Chip, string][]).map(([v, l]) => (
            <button key={v} className={chip === v ? "on" : ""} onClick={() => setChip(v)}>{l}</button>
          ))}
        </div>
        <select className="mo-select sort" value={sort} onChange={(e) => setSort(e.target.value as Sort)}>
          <option value="code">Sıralama: Kod</option>
          <option value="recent">Sıralama: Son sipariş</option>
          <option value="cost">Sıralama: Birim maliyet</option>
          <option value="margin">Sıralama: Marj</option>
          <option value="orders">Sıralama: Sipariş sayısı</option>
        </select>
        {filterOn && <button className="mo-clear" onClick={clearFilters}>{I.filterOff} Temizle</button>}
      </section>

      <div className="mo-count"><span><b>{filtered.length}</b> model{filtered.length !== models.length && <em> / {models.length}</em>}</span><span className="mo-hint">{view === "grid" ? "Karta tıklayınca detay açılır" : "Satıra tıklayınca detay açılır"}</span></div>

      {filtered.length === 0 ? (
        <section className="mo-card-blank"><div className="mo-blank"><div className="mo-blank-icon">{I.inbox}</div><b>{models.length === 0 ? "Henüz model yok" : "Eşleşen model yok"}</b>{models.length === 0 ? "Siparişler bir modele bağlanır; ilk modeli oluşturarak başlayın." : "Arama ya da süzgeçleri gevşetmeyi deneyin."}{models.length === 0 && has("models:create") ? <button className="mo-btn primary" onClick={openNew}>{I.plus} İlk modeli oluştur</button> : filterOn ? <button className="mo-btn" onClick={clearFilters}>Süzgeçleri temizle</button> : null}</div></section>
      ) : view === "grid" ? (
        <section className={`mo-grid ${busy ? "dim" : ""}`}>{visible.map(card)}</section>
      ) : (
        <section className={`mo-table-card ${busy ? "dim" : ""}`}>
          <div className="mo-table-wrap">
            <table className="mo-table">
              <thead><tr><th className="thumb" /><th>Model</th><th className="hide-md">Firma</th><th className="hide-lg">Kategori · sezon</th><th className="num">Sipariş</th><th className="num">Birim maliyet</th><th className="num hide-md">Son satış</th><th className="hide-sm">Marj</th><th className="act" /></tr></thead>
              <tbody>{visible.map(row)}</tbody>
            </table>
          </div>
        </section>
      )}

      {shown < filtered.length && (
        <div className="mo-more">
          <button className="mo-btn" onClick={() => setShown((s) => s + PAGE)}>{Math.min(PAGE, filtered.length - shown)} model daha göster</button>
          <span className="mo-hint">{visible.length} / {filtered.length}</span>
        </div>
      )}

      {/* ===== Detay çekmecesi ===== */}
      {detail && (
        <div className="mo-overlay" onClick={() => setDetail(null)}>
          <aside className="mo-drawer" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <div className="mo-drawer-head">
              <div className="mo-drawer-title">
                <span className="mo-kicker">{detail.firmName || "Firma atanmamış"}{detail.firmCode ? ` · ${detail.firmCode}` : ""}</span>
                <h2>{detail.modelName}</h2>
                <div className="mo-badges"><code className="mo-code-pill">{detail.modelCode}</code>{detail.patternCode && <code className="mo-code-pill muted">Desen {detail.patternCode}</code>}{detail.activeOrderCount > 0 && <span className="mo-tag live">{detail.activeOrderCount} aktif sipariş</span>}</div>
              </div>
              <button className="mo-ib big" aria-label="Kapat" onClick={() => setDetail(null)}>{I.close}</button>
            </div>
            <div className="mo-drawer-body">
              {/* galeri */}
              <div className="mo-gallery">
                {detail.imageUrls.length > 0 ? (
                  <>
                    <div className="mo-gallery-main" onClick={() => setLightbox(getImageUrl(detail.imageUrls[detailImg]))}>
                      <Img src={getImageUrl(detail.imageUrls[detailImg])} alt={detail.modelName} fallback={<div className="mo-gallery-broken"><span className="mo-mono">{initials(detail.modelCode)}</span><b>Görsel bulunamadı</b>Dosya sunucuda yok ya da taşınmış.</div>} />
                      {detail.imageUrls.length > 1 && <>
                        <button className="mo-gallery-nav l" aria-label="Önceki" onClick={(e) => { e.stopPropagation(); setDetailImg((i) => (i - 1 + detail.imageUrls.length) % detail.imageUrls.length); }}>{I.chevL}</button>
                        <button className="mo-gallery-nav r" aria-label="Sonraki" onClick={(e) => { e.stopPropagation(); setDetailImg((i) => (i + 1) % detail.imageUrls.length); }}>{I.chevR}</button>
                        <span className="mo-gallery-idx">{detailImg + 1} / {detail.imageUrls.length}</span>
                      </>}
                    </div>
                    {detail.imageUrls.length > 1 && (
                      <div className="mo-thumbs">{detail.imageUrls.map((u, i) => <button key={u} className={i === detailImg ? "on" : ""} onClick={() => setDetailImg(i)}><img src={getImageUrl(u)} alt="" loading="lazy" onError={(e) => { (e.currentTarget.parentElement as HTMLElement).classList.add("broken"); }} /></button>)}</div>
                    )}
                  </>
                ) : (
                  <div className="mo-gallery-empty" style={{ ["--mo-hue" as any]: hue(detail.modelCode) }}><span className="mo-mono">{initials(detail.modelCode)}</span><b>Fotoğraf yok</b>Siparişe görsel eklendiğinde burada görünür.</div>
                )}
              </div>

              {/* maliyet özeti */}
              <section className="mo-sec">
                <h3>Maliyet</h3>
                {detail.lastCostTotalTry != null ? (
                  <>
                    <div className="mo-stats">
                      <div><span>Birim maliyet</span><b>{detail.lastCostHasUnknownRate ? "~" : ""}{money(detail.lastCostUnitTry)}</b><small>{formatNumber(detail.lastCostQuantity || 0, 0)} adet · {fmtDate(detail.lastCostDate)}</small></div>
                      <div><span>Set toplamı</span><b>{money(detail.lastCostTotalTry)}</b><small>{detail.lastCostItemCount} kalem · {detail.costOrderCount} sipariş setinden sonuncusu</small></div>
                      <div><span>Önerilen satış</span><b>{money(detail.lastCostFinalUnitTry)}</b><small>+%{formatNumber(rates.overhead, 0)} genel gider · +%{formatNumber(rates.profit, 0)} kâr</small></div>
                      <div><span>Son satış</span><b>{(detail.lastOrderPrice ?? detail.price) != null ? money(detail.lastOrderPrice ?? detail.price, detail.lastOrderPrice != null ? detail.lastOrderCurrency : detail.priceCurrency) : "—"}</b><small>{marginBadge(detail) || "marj hesaplanamadı"}</small></div>
                    </div>
                    {detail.lastCostHasUnknownRate && <p className="mo-form-hint warn">Bazı kalemlerde kur kaydı yok; TL toplam eksik olabilir.</p>}
                    {canCosts && (breakdown ? (
                      <div className="mo-breakdown">
                        {breakdown.cats.map((c) => (
                          <div key={c.name} className="mo-bd-cat">
                            <div className="mo-bd-head"><span>{c.name}</span><span className="mo-sub">{c.items.length} kalem</span><b>{money(c.total)}</b></div>
                            <div className="mo-bd-bar"><i style={{ width: `${breakdown.grand ? (c.total / breakdown.grand) * 100 : 0}%` }} /></div>
                            <ul>{c.items.map((it, i) => <li key={i}><span>{it.name}</span><span className="mo-sub">{it.qty}</span><span className="num">{it.total != null ? money(it.total) : `${money(it.amount, it.cur)} (kur yok)`}</span></li>)}</ul>
                          </div>
                        ))}
                      </div>
                    ) : <div className="mo-loading"><span className="mo-spin" /> Kırılım yükleniyor</div>)}
                  </>
                ) : (
                  <div className="mo-blank small"><b>Bu model için maliyet girilmemiş</b>Sipariş üzerinden maliyet kalemleri girildiğinde birim maliyet ve önerilen satış burada hesaplanır.</div>
                )}
                {canCosts && <div className="mo-actions"><button className="mo-btn" onClick={() => setCostsOf(detail)}>{I.cost} Tüm maliyet setleri</button></div>}
              </section>

              {/* bilgiler */}
              <section className="mo-sec">
                <h3>Bilgiler</h3>
                <dl className="mo-facts">
                  <div><dt>Kategori</dt><dd>{detail.category || "—"}</dd></div>
                  <div><dt>Sezon</dt><dd>{detail.season || "—"}</dd></div>
                  <div><dt>Renk</dt><dd>{detail.color || "—"}</dd></div>
                  <div><dt>Beden</dt><dd>{detail.size || "—"}</dd></div>
                  <div><dt>Üretim stoğu</dt><dd>{detail.productionStockName || "—"}</dd></div>
                  <div><dt>Tahmini üretim</dt><dd>{detail.estimatedProductionTime ? `${detail.estimatedProductionTime} gün` : "—"}</dd></div>
                  {detail.description && <div className="wide"><dt>Açıklama</dt><dd>{detail.description}</dd></div>}
                </dl>
              </section>

              {/* fiyat geçmişi */}
              <section className="mo-sec">
                <h3>Sipariş ve fiyat geçmişi</h3>
                {!detailPrices ? <div className="mo-loading"><span className="mo-spin" /> Yükleniyor</div>
                  : detailPrices.priceHistory.length === 0 ? <p className="mo-form-hint">{detail.orderCount ? `${detail.orderCount} sipariş var, fiyat girilmemiş.` : "Bu model için sipariş yok."}</p>
                  : (
                    <div className="mo-table-wrap tight"><table className="mo-table small"><thead><tr><th>Tarih</th><th>Firma</th><th className="num">Miktar</th><th className="num">Birim fiyat</th></tr></thead><tbody>
                      {detailPrices.priceHistory.slice(0, 8).map((p) => <tr key={p.orderId} className="static"><td className="nowrap">{fmtDate(p.acceptanceDate)}</td><td>{p.firmName || "—"}</td><td className="num">{formatNumber(p.quantity, 0)} {p.orderUnitName}</td><td className="num"><b>{money(p.price, p.priceCurrency)}</b></td></tr>)}
                    </tbody></table>{detailPrices.priceHistory.length > 8 && <div className="mo-sub" style={{ padding: "0.5rem 0.75rem" }}>… ve {detailPrices.priceHistory.length - 8} sipariş daha</div>}</div>
                  )}
              </section>

              <div className="mo-actions">
                {has("models:update") && <button className="mo-btn primary" onClick={() => openEdit(detail)}>{I.edit} Düzenle</button>}
                {has("models:delete") && <button className="mo-btn danger" onClick={() => remove(detail)}>{I.trash} Sil</button>}
              </div>
            </div>
          </aside>
        </div>
      )}

      {lightbox && (
        <div className="mo-lightbox" onClick={() => setLightbox(null)}>
          <button aria-label="Kapat" onClick={() => setLightbox(null)}>{I.close}</button>
          <img src={lightbox} alt="" onClick={(e) => e.stopPropagation()} />
        </div>
      )}

      {/* ===== Model modalı ===== */}
      {modal.open && (
        <div className="mo-overlay center" onClick={() => setModal((x) => ({ ...x, open: false }))}>
          <form className="mo-modal" onClick={(e) => e.stopPropagation()} onSubmit={save}>
            <div className="mo-modal-head"><div><span className="mo-kicker">Model</span><h3>{modal.editing ? "Modeli düzenle" : "Yeni model"}</h3></div><button type="button" className="mo-ib big" aria-label="Kapat" onClick={() => setModal((x) => ({ ...x, open: false }))}>{I.close}</button></div>
            <div className="mo-modal-body">
              <Field label="Firma" hint="zorunlu" error={modal.errors.firmId}>
                <FirmPickerField value={modal.form.firmId} autoFocus={!modal.editing} placeholder="Firma seçin" onChange={(f) => setF({ firmId: f?.firmId || "" })} />
              </Field>
              <div className="mo-grid2">
                <Field label="Model kodu" hint="zorunlu" error={modal.errors.modelCode}><input value={modal.form.modelCode} onChange={(e) => setF({ modelCode: e.target.value })} placeholder="23811" /></Field>
                <Field label="Model adı" hint="zorunlu" error={modal.errors.modelName}><input value={modal.form.modelName} onChange={(e) => setF({ modelName: e.target.value })} placeholder="Klasik gömlek" /></Field>
              </div>
              <Field label="Açıklama"><textarea rows={2} value={modal.form.description} onChange={(e) => setF({ description: e.target.value })} placeholder="İsteğe bağlı" /></Field>
              <Field label="Kapak görseli" hint="sipariş görsellerinden seçin ya da adres yapıştırın">
                {modal.editing && modal.editing.imageUrls.length > 0 && (
                  <div className="mo-pick">
                    {modal.editing.imageUrls.map((u) => <button type="button" key={u} className={modal.form.imageUrl === u ? "on" : ""} onClick={() => setF({ imageUrl: modal.form.imageUrl === u ? "" : u })}><img src={getImageUrl(u)} alt="" loading="lazy" onError={(e) => { (e.currentTarget.parentElement as HTMLElement).classList.add("broken"); }} /></button>)}
                  </div>
                )}
                <input value={modal.form.imageUrl} onChange={(e) => setF({ imageUrl: e.target.value })} placeholder="/uploads/… veya https://…" />
              </Field>
              {hasModule("stock") && (
                <Field label="Üretim stoğu" hint="mamül; sipariş tamamlanınca reçete tüketimi için">
                  <select value={modal.form.productionStockId} onChange={(e) => setF({ productionStockId: e.target.value })}>
                    <option value="">Bağlı değil</option>
                    {mamul.map((s) => <option key={s.id} value={s.id}>{s.productCode} · {s.productName}</option>)}
                  </select>
                </Field>
              )}
            </div>
            <div className="mo-modal-foot"><button type="button" className="mo-btn" onClick={() => setModal((x) => ({ ...x, open: false }))}>Vazgeç</button><button type="submit" className="mo-btn primary" disabled={busy}>{busy ? "Kaydediliyor" : modal.editing ? "Kaydet" : "Modeli oluştur"}</button></div>
          </form>
        </div>
      )}

      {costsOf && (
        <ModelCostsModal
          modelId={costsOf.modelId}
          title={[costsOf.modelCode, costsOf.modelName].map((s) => (s || "").trim()).filter(Boolean).join(" · ") || undefined}
          onClose={async () => {
            setCostsOf(null);
            // Maliyet eklenmiş/silinmiş olabilir; katalog özetini ve açık çekmeceyi tazele
            const ov = await modelService.overview().catch(() => null);
            if (ov) { setModels(ov.models); if (detail) { const fresh = ov.models.find((x) => x.modelId === detail.modelId); if (fresh) openDetail(fresh); } }
          }}
        />
      )}

      <AlertModal isOpen={alert.isOpen} message={alert.message} type={alert.type} onClose={() => setAlert((a) => ({ ...a, isOpen: false }))} />
      <ConfirmModal isOpen={confirm.isOpen} message={confirm.message} type="danger" onConfirm={confirm.onConfirm} onCancel={() => setConfirm((c) => ({ ...c, isOpen: false }))} />
    </div>
  );
};

export default Models;
